import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatNumber } from "@/lib/formatters";
import { purchasesByWeekday } from '@shared/analyticsReporting';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";


interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function PurchasesByDayChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data: rawData, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-daily-trend", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const dayData = purchasesByWeekday(rawData || []);

  const empty = !isLoading && !error && (!rawData || !rawData.some((row:{purchases:number|null})=>row.purchases!=null));

  return (
    <DashboardWidget title="Purchases by Day" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-purchases-day">No purchase data for this period</div>
      ) : (
        <div className="px-4 pb-3 h-full" style={{ background: "#F7F9FC" }} data-testid="chart-purchases-by-day">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dayData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE3ED" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#5F6D82" }} />
              <YAxis tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={formatNumber} width={40} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }} formatter={(v: number) => [formatNumber(v), "Purchases"]} />
              <Bar dataKey="purchases" fill="var(--analytics-chart-primary, #7b1f40)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </DashboardWidget>
  );
}
